import { TagIcon, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Datatable } from '@@/datatables';
import { useTableStateWithStorage } from '@@/datatables/useTableState';
import { Button } from '@@/buttons';
import { withMeta } from '@@/datatables/extend-options/withMeta';
import { mergeOptions } from '@@/datatables/extend-options/mergeOptions';

import { Tag } from './types';
import { useColumns } from './columns/useColumns';
import { DeleteTagsModal } from './DeleteTagsModal';

export function TagsDatatable({
  dataset,
  canDelete,
  canUpdate,
  actionsDisabled,
  onRemove,
}: {
  dataset?: Tag[];
  canDelete: boolean;
  canUpdate: boolean;
  actionsDisabled: boolean;
  onRemove: (tags: Tag[]) => Promise<void>;
}) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Tag[]>([]);
  const tableState = useTableStateWithStorage('registryRepositoryTags', 'name');
  const columns = useColumns(canDelete || canUpdate);

  function handleDeleteClick(items: Tag[]) {
    setSelectedItems(items);
    setIsDeleteModalOpen(true);
  }

  async function handleDeleteConfirm() {
    await onRemove(selectedItems);
    setSelectedItems([]);
  }

  return (
    <>
      <Datatable
        title="Tags"
        titleIcon={TagIcon}
        columns={columns}
        dataset={dataset || []}
        isLoading={!dataset}
        settingsManager={tableState}
        renderTableActions={(selectedItems) =>
          canDelete && (
            <Button
              color="dangerlight"
              onClick={() => handleDeleteClick(selectedItems)}
              disabled={actionsDisabled || selectedItems.length === 0}
              icon={Trash2}
              data-cy="remove-registry-tags-button"
            >
              Remove
            </Button>
          )
        }
        getRowId={(tag) => tag.Name}
        extendTableOptions={mergeOptions(
          withMeta({
            canUpdate,
            actionsDisabled,
            table: 'registry-repository-tags',
          })
        )}
        data-cy="registry-tags-datatable"
      />

      <DeleteTagsModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        selectedTags={selectedItems.map((tag) => tag.Name)}
        onDeleteTags={handleDeleteConfirm}
      />
    </>
  );
}
